/** @jsxImportSource ./simboluri-jsx */
// TABELUL COMBINAȚIILOR unui ax de fundație (Pasul 17): fiecare combinație
// cu forța verticală, momentele pe cele două direcții și ponderile lor,
// excentricitatea, presiunile p₁/p₂ față de pragul metodei, citirea NP 112
// (μ, p_max) față de pragul ei, aria activă a tălpii și presiunea din model.
//
// Trăiește separat fiindcă îl citesc două ecrane — pagina fundației (axul
// ales din listă) și pagina axului (cardul «Fundația») — și trebuie să
// arate aceleași coloane, în aceeași ordine, oriunde ar fi deschis.

import { numar, useT } from "../i18n";
import type { AxFundatie } from "./tipuri";

/** Un număr cu zecimale fixe, în convenția limbii (12,50 / 12.50). */
function n(v: number | null | undefined, d = 1) {
  return numar(v, { minimumFractionDigits: d, maximumFractionDigits: d });
}

/** ✓ / ✗ / — pentru un verdict boolean (nul = necalculat). */
export function Da({ ok }: { ok?: boolean | null }) {
  if (ok == null) return <span className="sg-muted">—</span>;
  return <span className={ok ? "st-ok" : "st-bad"}>{ok ? "✓" : "✗"}</span>;
}

export default function TabelCombinatiiAx({ ax }: { ax: AxFundatie }) {
  const t = useT();
  const randuri = (ax.combinatii ?? []).filter((c) => c.p1_kpa != null);
  if (!randuri.length) {
    return <div className="sg-muted">{t("struct.tca.fara_combinatii")}</div>;
  }
  return (
    <table className="sg-table" data-testid="tabel-combinatii-ax">
      <thead>
        <tr>
          <th>{t("struct.rzv.combinatie")}</th><th>{t("struct.rzv.th_grupare")}</th>
          <th>ΣF_Z [kN]</th><th>M_long [kNm]</th><th>M_transv [kNm]</th>
          <th>w_l / w_t</th>
          <th>e_L/L</th><th>p₁ [kPa]</th><th>p₂ [kPa]</th>
          <th>{t("struct.rzv.th_prag")}</th><th></th>
          <th>{t("struct.rzv.th_tip")}</th><th>μ</th><th>p_max NP</th>
          <th>{t("struct.rzv.th_prag")} NP</th><th></th>
          <th>{t("struct.rzv.arie_activa")}</th><th>p model</th>
        </tr>
      </thead>
      <tbody>
        {randuri.map((c) => (
          <tr key={c.combinatie}>
            <td>{c.combinatie}</td>
            <td className="sg-muted">{c.grupa}</td>
            <td>{n(c.FZ_kn)}</td>
            <td className={(c.w_long ?? 1) > 0.05 ? "" : "sg-muted"}
                title={t("struct.rzv.pondere_long", { w: n(c.w_long, 2) })}>
              {n(c.M_long_knm, 2)}
            </td>
            <td className={(c.w_transv ?? 1) > 0.05 ? "" : "sg-muted"}
                title={t("struct.rzv.pondere_transv", { w: n(c.w_transv, 2) })}>
              {n(c.M_transv_knm, 2)}
            </td>
            <td className="sg-muted">{n(c.w_long, 2)} / {n(c.w_transv, 2)}</td>
            <td>{n(c.e_L_pe_L, 3)}</td>
            <td>{n(c.p1_kpa)}</td>
            <td>{n(c.p2_kpa)}</td>
            <td>{n(c.prag_metoda_kpa)}</td>
            <td><Da ok={c.ok_metoda} /></td>
            <td className="sg-muted">{c.tip_excentricitate}</td>
            <td>{n(c.mu, 3)}</td>
            <td>{n(c.p_max_np112_kpa)}</td>
            <td>{n(c.prag_np112_kpa)}</td>
            <td><Da ok={c.ok_np112} /></td>
            <td className={(c.desprindere_pct ?? 0) > 0.5 ? "st-bad" : ""}>
              {n(c.arie_activa_pct, 0)}%
            </td>
            <td>{n(c.p_max_model_kpa)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
